/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/defaultEmailEditor.scss';

import {AwesomeIcon, ChangeFunction, Checkbox, getScreenHeight, Loader, remToPx} from '@dvrd/dvr-controls';
import React, {ChangeEventHandler, MouseEventHandler, useEffect, useMemo, useRef, useState} from 'react';
import {EditMode, EmailFlow, UnlayerDesign} from '../../../../util/interfaces';
import DefaultEmail from '../../../../models/defaultEmailModel';
import {BolmateButton, BolmateInput} from '../../../controls';
import BolmateSwitch from '../../../controls/switch/bolmateSwitch';
import EmailEditor, {EditorRef} from 'react-email-editor';
import {getMergeTags} from '../../../../util/unlayerUtil';
import TestMailController, {TestMailRef} from "../../../email/test/testMailController";

interface Props {
    onClickBack: MouseEventHandler;
    onChangeName: ChangeFunction<string>;
    onChangeSubject: ChangeFunction<string>;
    onSelectFlow: (flow: EmailFlow) => ChangeFunction<boolean>;
    onChangeStatus: ChangeFunction<boolean>;
    onSubmit: (design: UnlayerDesign) => void;
    onChoosePreview: ChangeEventHandler<HTMLInputElement>;
    onDeletePreview: MouseEventHandler;
    onOpenPreview: MouseEventHandler;
    onClickDelete: MouseEventHandler;
    onTestEmail: (content: string, email: string) => void;
    onPreviewError: VoidFunction;
    defaultEmail: DefaultEmail;
    loading: boolean;
    previewFile: File | null;
    dataMode: EditMode;
}

export default function DefaultEmailEditor(props: Props) {
    const {
        onClickBack, onChangeName, onSelectFlow, onChangeStatus, onSubmit, onChoosePreview, onDeletePreview,
        defaultEmail, loading, previewFile, onChangeSubject, onClickDelete, dataMode, onOpenPreview, onTestEmail,
        onPreviewError
    } = props;
    const [editorLoading, setEditorLoading] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const toggleLabel = useMemo(() => {
        return defaultEmail.enabled ? 'Ingeschakeld' : 'Uitgeschakeld';
    }, [defaultEmail.enabled]);
    const previewInputRef = useRef<HTMLInputElement>(null);
    const editorRef = useRef<EditorRef>(null);
    const testMailRef = useRef<TestMailRef>(null);
    const htmlSet = useRef<boolean>(false);

    function onTogglePreview(evt: React.MouseEvent) {
        evt.stopPropagation();
        setShowPreview(!showPreview);
    }

    function onEditorLoaded() {
        setEditorLoading(false);
    }

    function onClickPreview() {
        previewInputRef.current?.click();
    }

    function onClickTest() {
        testMailRef.current?.activate();
    }

    function _onTest(email: string) {
        editorRef.current?.editor?.exportHtml((data: {html: string}) => {
            onTestEmail(data.html, email);
        });
    }

    function _onSubmit() {
        editorRef.current?.editor?.exportHtml((data: {design: UnlayerDesign}) => {
            onSubmit(data.design);
        });
    }

    function setDesign() {
        if (htmlSet.current || !editorRef.current) return;
        const design = defaultEmail.jsonTemplate;
        editorRef.current?.editor?.loadDesign(design);
        editorRef.current?.editor?.setMergeTags(getMergeTags(null));
        if (Object.keys(design).length)
            htmlSet.current = true;
    }

    function renderTopBar() {
        return (
            <div className='top-bar'>
                <div className='col'>
                    <div className='btn-back' onClick={onClickBack}>
                        <AwesomeIcon name='arrow-left' className='back-icon'/>
                    </div>
                    <div className='inputs-col'>
                        <BolmateInput onChange={onChangeName} value={defaultEmail.templateName} label='Template naam'
                                      placeholder='Naam van de template'/>
                        <BolmateInput onChange={onChangeSubject} value={defaultEmail.emailSubject}
                                      label='Onderwerp van mail' placeholder='Standaard onderwerp van de e-mail'/>
                    </div>
                    <div className='options-container'>
                        <div className='options-row'>
                            <div className='flow-container'>
                                <label className='flow-title'>Flows</label>
                                <Checkbox checked={defaultEmail.emailFlows.includes(EmailFlow.INVOICE)}
                                          onCheck={onSelectFlow(EmailFlow.INVOICE)} label='Factuur'/>
                                <Checkbox checked={defaultEmail.emailFlows.includes(EmailFlow.REVIEW_INVOICE)}
                                          onCheck={onSelectFlow(EmailFlow.REVIEW_INVOICE)} label='Review + Factuur'/>
                                <Checkbox checked={defaultEmail.emailFlows.includes(EmailFlow.CREDIT_INVOICE)}
                                          onCheck={onSelectFlow(EmailFlow.CREDIT_INVOICE)} label='Creditfactuur'/>
                            </div>
                            <BolmateSwitch value={defaultEmail.enabled} onChange={onChangeStatus} mainLabel='Status'
                                           labels={['', toggleLabel]} activeGreen/>
                        </div>
                        <div className='options-row'>
                            <BolmateButton onClick={onClickTest} label='Test verzenden'
                                           ornaments={{element: <AwesomeIcon name='paper-plane'/>}} secondary/>
                            {dataMode === EditMode.EDIT &&
                                <BolmateButton onClick={onClickDelete} label='Template verwijderen'
                                               ornaments={{element: <AwesomeIcon name='trash-alt'/>}}
                                               disabled={defaultEmail.enabled} red secondary/>}
                        </div>
                    </div>
                </div>
                {renderPreview()}
                <BolmateButton onClick={_onSubmit} label='Opslaan'/>
            </div>
        );
    }

    function renderPreview() {
        if (!showPreview) return <BolmateButton onClick={onTogglePreview} label='Preview opties'
                                                ornaments={{element: <AwesomeIcon name='cog'/>}} secondary/>;
        let previewError: boolean = false;
        let imageURL: string | null;
        if (previewFile)
            imageURL = URL.createObjectURL(previewFile);
        else {
            previewError = defaultEmail.previewURLError;
            imageURL = defaultEmail.previewURL;
        }
        return (
            <div className='preview-container' onClick={onClickPreview}>
                <input ref={previewInputRef} type='file' style={{display: 'none'}} onChange={onChoosePreview}/>
                {(!!imageURL && !previewError) ?
                    <img src={imageURL} alt='' className='preview-img' onError={onPreviewError}/> :
                    <AwesomeIcon name='image' className='preview-placeholder-icon'/>
                }
                <div className='preview-actions'>
                    <AwesomeIcon name='times' onClick={onTogglePreview} className='preview-action hide'
                                 title='Verbergen'/>
                    <div className='right'>
                        {!!imageURL && <>
                            <AwesomeIcon name='search' onClick={onOpenPreview} className='preview-action view'/>
                            <AwesomeIcon name='trash-alt' onClick={onDeletePreview} className='preview-action delete'/>
                        </>}
                    </div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (defaultEmail.jsonTemplate && !editorLoading)
            setDesign();
    }, [defaultEmail, editorLoading]);

    const screenHeight = getScreenHeight();
    const headerHeight = document.querySelector('div.bolmate-header')?.getBoundingClientRect().height ?? 0;
    const editorHeight = screenHeight - headerHeight - remToPx(19);
    return (
        <>
            <div className='default-template-editor'>
                <Loader active={loading}/>
                {renderTopBar()}
                <div className='html-editor-container'>
                    <div style={editorLoading ? {display: 'none'} : undefined}>
                        <EmailEditor ref={editorRef} onLoad={onEditorLoaded} projectId={43580} minHeight={editorHeight}
                                     options={{
                                         features: {
                                             // @ts-ignore
                                             smartMergeTags: false
                                         }
                                     }}/>
                    </div>
                </div>
            </div>
            <TestMailController onSubmit={_onTest} ref={testMailRef} closeOnSubmit/>
        </>
    );
}